/**
 * browserdav/server.js — the headless BrowserDAV server.
 *
 * The WebDAV engine (webdav/*, WebDAVManager.js) extracted from the
 * ExploratoryModels BrowserDAV app, driven by config instead of UI
 * (project: browserdav-as-an-acequia-js-capability, 2026-09-19). This file
 * is the re-authored middle of the app's main.js: everything between the
 * group route handler and WebDAVManager.handleRequest — auth gate, sidecar
 * public access, dotfile policy, COPY/MOVE two-leg authorization, the
 * /status and /auth/verify parity endpoints, and the directory-JSON probe
 * the service worker's mounts depend on.
 *
 * What it deliberately does NOT carry from the app: the file-browser HTML
 * shell, index.html serving, ?acequia-html/?acequia-editor, token panels,
 * peer grant prompts — those are UI, owned by the <acequia-browserdav>
 * element and the app.
 *
 * Auth posture (owner-agreed 2026-09-19):
 *   - Closed by default. `auth: { require: false }` must be written out to
 *     serve anonymously (the owner-bypass lesson).
 *   - `.acequia-access.json` sidecars are observed: a closed share can mark
 *     a directory anonymously readable (read: "anonymous", recursive,
 *     denyPatterns) — same vocabulary as the localWebDAV domain servers.
 *   - Tokens are chain tokens rooted in this device's own user; opaque
 *     CUID2 ids resolve through the local wallet. Foreign-rooted chains are
 *     refused (self-sovereign chain authority).
 *   - Posture is local config, advertised once as route-registry metadata —
 *     never group shared state (self-sovereign-browserdav decision).
 */

import { WebDAVManager } from './WebDAVManager.js'
import { sanitizeRequestForLog } from './webdav/WebDAVFileSystem.js'
import {
    buildStatusResponse,
    buildVerifyResponse,
    buildVerifyBadRequest,
    buildAuthNotServedResponse,
} from './endpoints.js'
import { forwardedHrefBase, instanceRootUrl } from './urlNamespace.js'
import {
    isBrowserNavigation,
    looksLikeDirectory,
    fileBrowserPage,
    plainIndexPage,
    editorPage,
} from './pages.js'
import { createKVStore } from './webdav/persistence.js'
import * as chains from '../../auth/chains.js'
import * as revocations from '../../auth/revocations.js'
import * as cookies from '../../auth/cookies.js'
import {
    acequiaReady,
    getInstanceId,
    getUserId,
    getDiscoveryOrigin,
} from '../acequia.js'
import { Group } from '../groups.js'
import { davTrace } from './webdav/log.js'

// Dotfile names allowed in listings for all users (read-only visibility)
const DOTFILE_READER_ALLOWLIST = ['.well-known', '.ai', '.acequia-access.json']

const NAME_REGEX = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/
const RESERVED_NAMES = new Set(['all', 'any', 'random', 'index', 'status', 'auth', 'styles', 'components'])

// Request paths arrive percent-encoded; the filesystem decodes once at entry
// and hands share-relative names back decoded, so anything compared against
// them must be decoded the same way (a malformed escape stays literal).
function safeDecodePath(p) {
    try { return decodeURIComponent(p) } catch { return p }
}

function escapeXml(text) {
    if (!text) return ''
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
}


function decodeURIComponentSafe(value) {
    try { return decodeURIComponent(value) } catch { return value }
}

export class BrowserDAVServer {
    /**
     * @param {object} options
     * @param {string|object} options.group - Group name to create, or an
     *   existing Group instance to serve on.
     * @param {string} [options.serverName] - Stable route slug (lowercase,
     *   digits, hyphens). Without it only the /{instanceId}/ routes exist.
     * @param {string} [options.displayName]
     * @param {Array}  [options.shares] - [{ source: 'opfs', path, name? } |
     *   { source: 'fsa', handle, name? }]. OPFS shares need no gesture.
     * @param {object} [options.auth]
     * @param {boolean} [options.auth.require=true] - Closed by default.
     *   `false` serves every request with full access — write it deliberately.
     * @param {string}  [options.auth.authUrl] - Chain resolution endpoint;
     *   defaults to `${location.origin}/auth`.
     * @param {string}  [options.auth.owner] - The user id whose chain tokens
     *   open this server. Defaults to this context's own user. A HEADLESS
     *   page — a fixture in a fresh browser context — has a user too, but a
     *   throwaway one: acequia.js mints a local identity on first load, and
     *   nobody can mint tokens for it (it has no session with the auth
     *   server). A server rooted in it refuses every real token while
     *   reporting nothing wrong. Declare the owner instead: they mint tokens
     *   in their own session and hand them to the consumer; the headless
     *   server needs only the owner's public id. An explicit owner always
     *   wins — declaring it is the deliberate act, and the page's own
     *   identity says nothing about whose tokens it should accept.
     * @param {boolean} [options.persist=true] - Restore shares from and save
     *   shares to the function-owned origin store.
     * @param {string}  [options.dbName='acequia-browserdav'] - Store name —
     *   the opt-out: give a consumer its own name to isolate its shares.
     * @param {function} [options.onShareNeedsGesture] - Called with each
     *   persisted FSA share whose permission needs a user gesture to
     *   re-grant. Headless callers may ignore FSA entirely.
     * @param {function} [options.onActivity] - Called after each request the
     *   protocol core served — an app's activity indicator, nothing more.
     * @param {boolean} [options.servePages=true] - Answer a browser's
     *   navigation (Accept: text/html) with pages: a share's own index.html
     *   when it has one, else the file-browser page; `?acequia-html` and
     *   `?acequia-editor`. Off, a browser gets what a WebDAV client gets.
     */
    constructor(options = {}) {
        if (!options.group) throw new Error('browserdav: options.group is required')
        this.options = options
        // A Group instance keeps its name as `groupId` (groups.js:343); `name`
        // was a guess that read undefined into every emitted URL when an app
        // handed its own group in — found on the BrowserDAV app's first load
        // onto the extraction, 2026-09-20.
        this.groupName = typeof options.group === 'string'
            ? options.group
            : (options.group.groupId ?? options.group.name)
        this.serverName = options.serverName || null
        if (this.serverName) {
            if (!NAME_REGEX.test(this.serverName) || RESERVED_NAMES.has(this.serverName)) {
                throw new Error(`browserdav: invalid serverName '${this.serverName}'`)
            }
        }
        this.displayName = options.displayName || this.serverName || 'BrowserDAV'
        // Closed by default; auth: { require: false } must be explicit.
        this.authRequired = options.auth?.require !== false
        if (options.auth?.ownerBypass) {
            // The app removed its embedded owner token after it leaked to
            // anonymous visitors; the headless function refuses to grow one.
            throw new Error('browserdav: ownerBypass is not supported — mint a token instead')
        }
        this.group = null
        this._ownsGroup = typeof options.group === 'string'
        this.manager = new WebDAVManager()
        this.pendingShares = [] // persisted FSA shares awaiting a permission gesture
        this._startTime = Date.now()
        this._sidecarCache = new Map()
        this._registeredPatterns = new Set()
        this._store = options.persist === false
            ? null
            : createKVStore(options.dbName || 'acequia-browserdav', 'shares')
        this._keyResolver = null
        this._revocationChecker = null
        this._authUrl = null
        this._ownerUserId = null
        this._started = false
    }

    /** Absolute client-visible base URL for this server, ending in '/'. */
    get url() {
        return instanceRootUrl({
            // The group's own discovery override wins — a dev stack's page
            // origin and proxy origin differ, and the URL must be the proxy.
            discoveryOrigin: this.options.groupOptions?.discoveryURL || getDiscoveryOrigin(),
            groupName: this.groupName,
            segment: this._routeSegment(),
        })
    }

    /** The user id whose chain tokens open this server (resolved at start). */
    get owner() {
        return this._ownerUserId
    }

    /** The shares this server holds: [{ pathName, displayName, source, directoryHandle, … }]. */
    get shares() {
        return this.manager.getAllInstances()
    }

    _routeSegment() {
        return this.serverName || getInstanceId()
    }

    _routeMetadata(extra = {}) {
        // The posture advertisement. Published once at registration — route
        // metadata, not group shared state, so a hidden tab is never asked
        // per request and no leader election is needed to change it.
        return {
            webdav: true,
            serverName: this.serverName,
            displayName: this.displayName,
            authRequired: this.authRequired,
            ...extra,
        }
    }

    // ── lifecycle ────────────────────────────────────────────────────

    async start() {
        if (this._started) return this
        await acequiaReady()

        const authUrl = this.options.auth?.authUrl
            || (typeof window !== 'undefined' ? `${window.location.origin}/auth` : null)
        if (this.authRequired && !authUrl) {
            throw new Error('browserdav: auth.authUrl is required outside a window context when auth is on')
        }
        this._authUrl = authUrl

        // Who opens this server. Self-rooted acceptance compares every
        // verified chain's root against ONE user id; resolve it once here.
        // Found 2026-09-20: a fresh browser context is not identity-free —
        // acequia.js mints a throwaway user on first load — so a headless
        // server defaulted to an id nobody could mint tokens for and refused
        // every real one. The first headless verification (09-19) could not
        // see this: createToken failed first, so no valid token was ever
        // presented, and the 401 and sidecar paths it did exercise behaved.
        const ownUserId = getUserId() || null
        const declared = this.options.auth?.owner || null
        this._ownerUserId = declared || ownUserId
        if (declared && ownUserId && declared !== ownUserId) {
            console.info(`[browserdav] accepting tokens rooted in ${declared}, not this context's own user ${ownUserId}`)
        }
        if (this.authRequired && !this._ownerUserId) {
            throw new Error(
                'browserdav: no user in this context to root token acceptance in — pass auth.owner (the id of the user who mints its tokens), or auth: { require: false }'
            )
        }
        if (authUrl) {
            this._keyResolver = chains.createCombinedKeyResolver(authUrl)
            const checkers = [chains.createWalletRevocationChecker()]
            if (chains.createServerRevocationChecker) {
                checkers.push(chains.createServerRevocationChecker(authUrl))
            }
            if (revocations?.createStoredRevocationChecker) {
                checkers.push(revocations.createStoredRevocationChecker({
                    authUrl,
                    resolvePublicKey: this._keyResolver,
                }))
            }
            this._revocationChecker = chains.createCombinedRevocationChecker
                ? chains.createCombinedRevocationChecker(...checkers)
                : checkers[0]

            // Peer delivery of revocations. A peer is only ever offered
            // revocations for chains it has already shown us, so telling it
            // discloses nothing it did not already hold — the disclosure
            // boundary, not an optimisation. The same set bounds what it may
            // push at us. `send` resolves the group lazily: it may not exist yet.
            this.revocationGossip = revocations?.createRevocationGossip
                ? revocations.createRevocationGossip({
                    authUrl,
                    resolvePublicKey: this._keyResolver,
                    send: (peerId, msg) => this.group?.serverSendToPeer(peerId, msg),
                })
                : null
        }

        if (this._ownsGroup) {
            this.group = new Group(this.groupName, {
                displayName: this.displayName,
                serverName: this.serverName,
                capabilities: ['webdav'],
                serverProxiedRequestHandler: (data) => this.handleWebDAVRequest(data),
                // e.g. { discoveryURL } when the page's origin is not the
                // discovery server (a dev stack with split ports).
                ...(this.options.groupOptions || {}),
            })
            await this.group.ready
        } else {
            this.group = this.options.group
            await this.group.ready
        }

        // A signed revocation from any peer. Unlike an unsigned `accessRevoked`
        // (an app-level assertion whose only safe response is self-denial),
        // this one proves its own authority, so it can be accepted from a peer
        // with no standing and still not let that peer revoke anything it
        // could not have granted. Listened for on the group whether this
        // server created it or was handed it — the app that owns the group
        // keeps its other peer-message handling.
        if (this.revocationGossip && typeof this.group.on === 'function') {
            this._onPeerMessage = ({ peerId, message }) => {
                if (message?.type !== 'revocationShare') return
                const rtcPeer = this.group.getPeerByInstanceId?.(peerId)
                const peer = rtcPeer && rtcPeer._channel?.readyState === 'open'
                    ? rtcPeer
                    : { id: peerId, deviceId: message.deviceId }
                this.handleRevocationShare(peer, message)
            }
            this.group.on('peerMessage', this._onPeerMessage)
        }

        this._registerRootRoutes()

        if (this._store) await this._restoreShares()
        for (const spec of this.options.shares || []) {
            await this.addShare(spec)
        }

        this._started = true
        return this
    }

    /** Unregister every route this server registered. Closes the group only
     *  if start() created it. */
    async stop() {
        if (this._onPeerMessage && typeof this.group?.off === 'function') {
            try { this.group.off('peerMessage', this._onPeerMessage) } catch { /* gone */ }
        }
        for (const pattern of this._registeredPatterns) {
            try { this.group.unregisterRoute(pattern) } catch { /* group gone */ }
        }
        this._registeredPatterns.clear()
        if (this._ownsGroup) {
            try { this.group.close?.() } catch { /* already closed */ }
        }
        this._started = false
    }

    // ── shares ───────────────────────────────────────────────────────

    /**
     * Add a share. Spec: { source: 'opfs', path, name? } — created if absent,
     * no gesture needed — or { source: 'fsa', handle, name? } with a handle
     * the caller already holds (obtaining one needs a gesture; that is the
     * element's job, not this function's).
     * @returns {Promise<string>} the pathName the share serves under
     */
    async addShare(spec) {
        let handle
        let source = spec.source || (spec.handle ? 'fsa' : 'opfs')
        if (source === 'opfs') {
            if (!spec.path) throw new Error('browserdav: opfs share needs a path')
            const opfsRoot = await navigator.storage.getDirectory()
            handle = await opfsRoot.getDirectoryHandle(spec.path, { create: true })
        } else {
            if (!spec.handle) throw new Error('browserdav: fsa share needs a handle')
            handle = spec.handle
        }
        const pathName = await this.manager.addDirectory(handle, spec.name || null, { source })
        this._registerShareRoutes(pathName)
        if (this._store) {
            await this._store.set(pathName, {
                pathName,
                directoryHandle: source === 'fsa' ? handle : null,
                displayName: spec.name || handle.name,
                source,
                opfsName: source === 'opfs' ? spec.path : null,
                savedAt: new Date().toISOString(),
            })
        }
        return pathName
    }

    /** Remove a share and its routes (and its persisted record). */
    async removeShare(pathName) {
        const removed = this.manager.removeDirectory(pathName)
        for (const prefix of this._routePrefixes()) {
            const pattern = `${prefix}/${pathName}/*path`
            if (this._registeredPatterns.delete(pattern)) {
                try { this.group.unregisterRoute(pattern) } catch { /* group gone */ }
            }
        }
        if (this._store) await this._store.remove(pathName)
        return removed
    }

    /** Persisted FSA shares whose permission lapsed. Re-adding one needs a
     *  user gesture: call grantPendingShare(pathName) from a click handler. */
    async grantPendingShare(pathName) {
        const idx = this.pendingShares.findIndex((r) => r.pathName === pathName)
        if (idx === -1) return false
        const record = this.pendingShares[idx]
        const perm = await record.directoryHandle.requestPermission({ mode: 'readwrite' })
        if (perm !== 'granted') return false
        this.pendingShares.splice(idx, 1)
        await this.manager.addDirectory(record.directoryHandle, record.displayName, { source: 'fsa' })
        this._registerShareRoutes(record.pathName)
        return true
    }

    async _restoreShares() {
        let records = []
        try {
            records = (await this._store.entries()).map(([, value]) => value)
        } catch { return }
        for (const record of records) {
            try {
                if (record.source === 'opfs' && record.opfsName) {
                    const opfsRoot = await navigator.storage.getDirectory()
                    const handle = await opfsRoot.getDirectoryHandle(record.opfsName)
                    await this.manager.addDirectory(handle, record.displayName, { source: 'opfs' })
                    this._registerShareRoutes(record.pathName)
                } else if (record.directoryHandle) {
                    const perm = await record.directoryHandle.queryPermission({ mode: 'readwrite' })
                    if (perm === 'granted') {
                        await this.manager.addDirectory(record.directoryHandle, record.displayName, { source: 'fsa' })
                        this._registerShareRoutes(record.pathName)
                    } else {
                        this.pendingShares.push(record)
                        try { this.options.onShareNeedsGesture?.(record) } catch { /* caller's problem */ }
                    }
                }
            } catch (err) {
                console.warn(`[browserdav] could not restore share '${record?.pathName}':`, err?.message || err)
            }
        }
    }

    // ── routes ───────────────────────────────────────────────────────

    _routePrefixes() {
        const prefixes = [`/${getInstanceId()}`]
        if (this.serverName) prefixes.push(`/${this.serverName}`)
        return prefixes
    }

    /**
     * Register a set of routes as ONE announcement. A group that can batch
     * (`registerRoutes`) gets one send and the relay makes one broadcast;
     * eight single registrations made eight partial registry snapshots,
     * which the client's drift detector read as loss once its grace ran
     * out (seen from Node 2026-09-28, presumably in browsers all along). A
     * group without the batch method (the test fakes) gets them one by one.
     */
    _registerBatch(routes) {
        const fresh = routes.filter((r) => !this._registeredPatterns.has(r.pattern))
        if (fresh.length === 0) return
        if (typeof this.group.registerRoutes === 'function') {
            this.group.registerRoutes(fresh)
        } else {
            for (const r of fresh) this.group.registerRoute(r.pattern, r.handler, r.metadata)
        }
        for (const r of fresh) this._registeredPatterns.add(r.pattern)
    }

    _registerRootRoutes() {
        const rootHandler = (data) => this.handleRootRequest(data)
        const davHandler = (data) => this.handleWebDAVRequest(data)
        const restHandler = (data) => this._handleOutsideShares(data)
        const routes = []
        for (const prefix of this._routePrefixes()) {
            routes.push({ pattern: `${prefix}/`, handler: rootHandler, metadata: this._routeMetadata() })
            // Everything else under the server: the least specific route, so
            // shares, /status and /auth/verify (and any other app's routes on
            // this instance) still win. Without it a path outside every share
            // — a PUT at the root, the first thing a client tries on a server
            // with no shares yet — met the group router's RouteNotFoundError
            // as raw JSON, for any method and any grant (found 2026-10-06).
            routes.push({ pattern: `${prefix}/*path`, handler: restHandler, metadata: this._routeMetadata({ streaming: true }) })
            routes.push({ pattern: `${prefix}/status`, handler: davHandler, metadata: this._routeMetadata() })
            routes.push({ pattern: `${prefix}/auth/verify`, handler: davHandler, metadata: this._routeMetadata() })
        }
        this._registerBatch(routes)
    }

    /**
     * A request under the server that no more specific route took. The
     * catch-all also sorts ahead of the root's own route (its extra segment
     * outranks none), so the root, a share named without its trailing slash
     * and the parity endpoints are handed back to their handlers; only a
     * path outside every share is answered here, after the same gate as
     * everything else: 404 for a read (nothing lives there), 403 for a write
     * (the root holds only shares — RFC 4918 §9.3.1's "server does not allow
     * the creation … at the given location"), naming the shares to use.
     */
    async _handleOutsideShares(data) {
        const rest = String(data.remainingPath ?? '').replace(/^\/+/, '')
        const method = (data.method || 'GET').toUpperCase()
        if (data.bodyStream && method !== 'PUT') await this._bufferBodyStream(data)
        if (rest === '') return this.handleRootRequest(data)
        const first = decodeURIComponentSafe(rest.split('/')[0])
        if (first === 'status' || first === 'auth' || this.manager.webdavInstances.has(first)) {
            return this.handleWebDAVRequest(data)
        }

        const denied = await this._authorizeRequest(data)
        if (denied) {
            try { await data.bodyStream?.cancel?.() } catch { /* already closed */ }
            return denied
        }
        try { await data.bodyStream?.cancel?.() } catch { /* already closed */ }

        const shares = [...this.manager.webdavInstances.keys()]
        const where = shares.length
            ? `This server's files live in its shares: ${shares.map((n) => `${n}/`).join(', ')}.`
            : 'This server has no shares yet; add one to hold files.'
        const text = { 'Content-Type': 'text/plain; charset=utf-8' }
        if (method === 'OPTIONS') {
            return { status: 200, headers: { DAV: '1, 2', Allow: 'OPTIONS' }, body: '' }
        }
        if (['PUT', 'MKCOL', 'POST'].includes(method)) {
            return { status: 403, headers: text, body: `Forbidden: the root of this server holds only shares. ${where}` }
        }
        return { status: 404, headers: text, body: `Not Found: nothing lives outside a share. ${where}` }
    }

    _registerShareRoutes(pathName) {
        const davHandler = (data) => this.handleWebDAVRequest(data)
        const routes = []
        for (const prefix of this._routePrefixes()) {
            routes.push({
                pattern: `${prefix}/${pathName}/*path`,
                handler: davHandler,
                metadata: this._routeMetadata({ streaming: true }),
            })
            this.manager.registerRoute(`${prefix}/${pathName}`, pathName)
        }
        this._registerBatch(routes)
    }

    /**
     * A peer handed us a signed revocation. Verified before it counts, so a
     * hostile or mistaken sender cannot revoke anything by assertion; kept
     * only if it names a chain that peer had already shown us.
     */
    async handleRevocationShare(peer, message) {
        const g = this.revocationGossip
        if (!g) return { stored: false }
        try {
            const result = await g.receive(peer.id, message)
            if (result?.stored) {
                davTrace('[browserdav] learned a revocation from a peer — it now holds with the network gone')
            }
            return result
        } catch (err) {
            console.warn('[browserdav] could not accept peer revocation:', err?.message || err)
            return { stored: false, error: err?.message || String(err) }
        }
    }

    // ── tokens ───────────────────────────────────────────────────────

    /**
     * Mint an access credential for this server, headlessly — the operation
     * that used to require the app's UI (and, before the state-leader fix,
     * silently failed). A chain token rooted in this device's own user;
     * the returned `id` is the opaque CUID2 the wallet resolves, presented
     * as `Bearer {id}` or `?token={id}`.
     *
     * Scope paths are rooted under this server's route segment so the token
     * opens THIS server and not the same user's other instances
     * (route-rooted tokens — cross-instance reuse fix).
     *
     * @param {object} [opts]
     * @param {string} [opts.name]
     * @param {string} [opts.note]
     * @param {object} [opts.scope] - { paths, writePaths }, share-relative or
     *   pre-rooted; defaults to full access to this server.
     * @param {number} [opts.expiresInSeconds=30 days]
     * @returns {Promise<{ id, anchorHash, leafHash, url }>}
     */
    async createToken(opts = {}) {
        if (!this._authUrl) throw new Error('browserdav: cannot mint tokens without auth.authUrl')
        // Minting signs a root anchor with the user's own keys and stores it
        // with the user's session. A headless page has a throwaway user with
        // neither, and the failure used to surface three calls deep as
        // 'Authentication required'. When the server is rooted in someone
        // other than this context's user, say so and say where minting
        // belongs, rather than minting a token the server would then refuse.
        const own = getUserId() || null
        if (!own || (this._ownerUserId && this._ownerUserId !== own)) {
            throw new Error(
                `browserdav: createToken mints with this context's own keys and session (user ${own || 'none'}), but this server accepts tokens rooted in ${this._ownerUserId || 'nobody'} — mint from the owner's own session and pass the token to the consumer`
            )
        }
        const segment = this._routeSegment()
        const root = (p) => {
            if (!p || p === '*' || p === '/*') return `/${segment}/*`
            const withSlash = p.startsWith('/') ? p : `/${p}`
            return withSlash === `/${segment}` || withSlash.startsWith(`/${segment}/`)
                ? withSlash
                : `/${segment}${withSlash}`
        }
        const scope = opts.scope
            ? {
                paths: (opts.scope.paths || []).map(root),
                writePaths: (opts.scope.writePaths || []).map(root),
            }
            : { paths: [`/${segment}/*`], writePaths: [`/${segment}/*`] }
        const result = await chains.createChainToken({
            authUrl: this._authUrl,
            authToken: cookies.getEmbeddedToken?.() || null,
            name: opts.name || `browserdav ${segment}`,
            note: opts.note || null,
            scope,
            expiresInSeconds: opts.expiresInSeconds || 30 * 24 * 3600,
        })
        return {
            id: result.leafId,
            anchorHash: result.anchorHash,
            leafHash: result.leafHash,
            url: `${this.url}?token=${result.leafId}`,
        }
    }

    // ── request pipeline ─────────────────────────────────────────────

    /** Root listing: GET → JSON, PROPFIND → multistatus of the shares. */
    async handleRootRequest(data) {
        const method = (data.method || 'GET').toUpperCase()
        const denied = await this._authorizeRequest(data)
        if (denied) return denied
        if (method === 'GET') {
            if (this._servePages() && isBrowserNavigation(data)) {
                return this._fileBrowserPage('/', this.displayName, data)
            }
            return this._rootDirectoryJSON()
        }
        if (method === 'PROPFIND') return this._rootPropfind(data)
        if (method === 'OPTIONS') {
            return { status: 200, headers: { DAV: '1, 2', Allow: 'GET, PROPFIND, OPTIONS' }, body: '' }
        }
        return {
            status: 405,
            headers: { 'Content-Type': 'text/plain', Allow: 'GET, PROPFIND, OPTIONS' },
            body: 'Method Not Allowed',
        }
    }

    async handleWebDAVRequest(data) {
        davTrace('[browserdav] request:', sanitizeRequestForLog(data))

        // Streamed transport delivers the body as a live ReadableStream.
        // Only PUT consumes it incrementally; everything else gets the
        // buffered body it always had.
        if (data.bodyStream && (data.method || 'GET').toUpperCase() !== 'PUT') {
            await this._bufferBodyStream(data)
        }

        // Origin-parity endpoints: /status and /auth/verify answer before
        // auth and instance resolution; any other /auth/* path gets the
        // feature-detect 404 explainer unless a share actually claims it.
        {
            const endpointPath = this._getRequestPath(data)
            if (endpointPath === '/status' || endpointPath === '/status/') {
                return buildStatusResponse({
                    name: this.serverName || this.displayName,
                    startTime: this._startTime,
                })
            }
            if (endpointPath === '/auth/verify') {
                return await this._handleAuthVerify(data)
            }
            if (endpointPath.startsWith('/auth/') && !this.manager.resolveInstance(data.path || '')) {
                return buildAuthNotServedResponse(endpointPath)
            }
        }

        // Authorize BEFORE any branch that returns content. The file-browser
        // page below used to be served ahead of this check in the app, which
        // meant a request carrying Accept: text/html — every browser — skipped
        // authorization entirely.
        const denied = await this._authorizeRequest(data)
        if (denied) return denied

        // A person navigating: a directory gets its own index.html if it has
        // one (unless ?acequia-dir asks for the browser instead), else the
        // file-browser page. A file falls through to the normal GET.
        if (this._servePages() && isBrowserNavigation(data)) {
            const reqPath = this._getRequestPath(data)
            if (looksLikeDirectory(reqPath)) {
                if (data.query?.['acequia-dir'] === undefined) {
                    const index = await this._tryServeIndexHtml(data)
                    if (index) return index
                }
                const dirName = reqPath?.split('/').filter(Boolean).pop() || this.displayName
                return this._fileBrowserPage(reqPath || '/', dirName, data)
            }
        }

        if (!this.manager.hasActiveInstances()) {
            return { status: 503, headers: { 'Content-Type': 'text/plain' }, body: 'No WebDAV filesystems available' }
        }

        // A dotfile is readable only to those who may write there (secrets
        // such as .env). Refused like any other resource, with the status a
        // listing marks it with: 401 without a token, 403 with one. Until
        // 2026-10-06 this answered 404 and listings omitted the name — a
        // hidden-file convention WebDAV does not have; the node server
        // lists and refuses, and the two now match (owner decision: the
        // WebDAV/Plan 9 stance, BrowserDAV ↔ Nephele parity).
        const requestPath = this._getRequestPath(data)
        const basename = requestPath.split('/').filter(Boolean).pop()
        if (basename?.startsWith('.') && !DOTFILE_READER_ALLOWLIST.includes(basename)) {
            if (!chains.matchesPath(requestPath, data.user.writePaths || [])) {
                return this._extractToken(data)
                    ? { status: 403, headers: { 'Content-Type': 'text/plain' }, body: 'Forbidden' }
                    : { status: 401, headers: { 'Content-Type': 'text/plain', 'WWW-Authenticate': 'Bearer realm="Acequia"' }, body: 'Unauthorized' }
            }
        }

        data.entryFilter = this._makeEntryFilter(data)
        data.crossServer = {
            selfHosts: this._selfHosts(),
            // Pull mode's outbound guard: http and local hosts only when the
            // embedder says so (tests, a one-machine setup); a Node embedder
            // may add a resolver check.
            allowInsecure: this.options.allowInsecureTransfers === true,
            checkOutbound: this.options.checkOutbound || null,
            // How often a pull's 202 report carries a performance marker.
            markerIntervalMs: this.options.pullMarkerIntervalMs || undefined,
            // The Range resume's budget: how many re-opens, and the pause unit.
            maxResumes: this.options.pullMaxResumes || undefined,
            resumeBackoffMs: this.options.pullResumeBackoffMs || undefined,
        }
        data.authorizeTransfer = this._makeTransferAuthorizer(data)

        // The directory-JSON probe: the service worker sends the header on
        // EVERY mount GET and branches on whether the response echoes it.
        // A probe on a file declines and the normal GET serves it; the
        // ?acequia-json query form names a listing and 404s on a file.
        const method = (data.method || 'GET').toUpperCase()
        if (method === 'GET') {
            const jsonByQuery = data.query?.['acequia-json'] !== undefined
            const jsonByHeader = !!(data.headers?.['x-acequia-directory-json']
                || data.headers?.['x-directory-as-json'])
            if (jsonByQuery || jsonByHeader) {
                const resp = await this._handleAcequiaJson(data, { probe: jsonByHeader && !jsonByQuery })
                if (resp) return resp
            }
            if (this._servePages()) {
                if (data.query?.['acequia-html'] !== undefined) return this._handleAcequiaHtml(data)
                if (data.query?.['acequia-editor'] !== undefined) return this._handleAcequiaEditor(data)
            }
        }

        try {
            const response = await this.manager.handleRequest(data)
            try { this.options.onActivity?.() } catch { /* the app's problem */ }
            return response
        } catch (error) {
            console.error('[browserdav] request error:', error)
            return {
                status: 500,
                headers: { 'Content-Type': 'text/plain' },
                body: error.message || 'Internal server error',
            }
        }
    }

    // ── authorization ────────────────────────────────────────────────

    /**
     * Sets data.user and returns null when the request may proceed; returns
     * the response to send when it may not. Anonymous access exists only
     * where a .acequia-access.json sidecar grants it — read scope, no writes.
     */
    async _authorizeRequest(data) {
        if (!this.authRequired) {
            data.user = { paths: ['*'], writePaths: ['*'], isAnonymous: false }
            return null
        }

        const authResult = await this._checkRequestAuth(data)
        if (authResult.allowed) {
            data.user = authResult.user || { paths: ['*'], writePaths: ['*'], isAnonymous: false }
            return null
        }

        // Sidecars grant anonymous READ only — but they do AUTHENTICATE the
        // visitor (as nephele's `nobody`: the sidecar's read paths, no write
        // paths), for any method. What differs by method is the verdict:
        //   - reads pass;
        //   - COPY/MOVE pass the gate on the SOURCE leg (a read) and are
        //     judged per leg by the transfer authorizer below, which refuses
        //     a user with no write paths at the destination (bind) or the
        //     source (unbind) — 403;
        //   - every other write is refused here, 403, because the protocol
        //     core beneath never consults data.user and this gate is the
        //     whole wall. `write: "anonymous"` is a planned sidecar extension
        //     on the node server and deliberately not honoured here either.
        // 403 rather than 401 is parity: the node server authenticates the
        // sidecar visitor and then forbids the write (jwtAuth canWrite), and
        // the conformance rows moved here on 2026-09-20 assert exactly that.
        const method = (data.method || 'GET').toUpperCase()
        const sidecarScope = await this._checkSidecarAccess(data)
        if (sidecarScope) {
            data.user = { paths: sidecarScope.paths, writePaths: [], isAnonymous: true }
            if (['GET', 'HEAD', 'PROPFIND', 'OPTIONS', 'COPY', 'MOVE'].includes(method)) return null
            return {
                status: 403,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ error: 'Public access is read-only: a sidecar grants no write privilege' }),
            }
        }

        const status = authResult.reason?.includes('No token') ? 401 : 403

        // A person navigating in a browser gets the file-browser shell rather
        // than raw JSON, so they can see why access failed and supply a token.
        // The shell is served with NO token and grants nothing — every data
        // request it makes still comes back through this gate. That is the
        // distinction the old owner-bypass got wrong: serving a PAGE to an
        // unauthenticated visitor is fine, serving them a CREDENTIAL is not.
        // The node server does the same, redirecting browsers to its
        // registration page instead of answering them with JSON.
        if (this._servePages() && isBrowserNavigation(data)) {
            const reqPath = this._getRequestPath(data) || '/'
            const dirName = reqPath.split('/').filter(Boolean).pop() || this.displayName
            const page = this._fileBrowserPage(reqPath, dirName, { ...data, headers: {}, query: {} })
            return {
                ...page,
                status,
                headers: {
                    ...page.headers,
                    ...(status === 401 ? { 'WWW-Authenticate': 'Bearer realm="Acequia"' } : {}),
                },
            }
        }

        const headers = { 'Content-Type': 'application/json' }
        // Parity: challenge like the node server so standard DAV clients can
        // negotiate auth.
        if (status === 401) headers['WWW-Authenticate'] = 'Bearer realm="Acequia"'
        return { status, headers, body: JSON.stringify({ error: authResult.reason || 'Access denied' }) }
    }

    async _checkRequestAuth(data) {
        const method = (data.method || 'GET').toUpperCase()

        // OPTIONS is always allowed (CORS/WebDAV discovery)
        if (method === 'OPTIONS') {
            return { allowed: true, user: { paths: ['*'], writePaths: ['*'], isAnonymous: false } }
        }

        const token = this._extractToken(data)
        if (!token) return { allowed: false, reason: 'No token provided' }

        // Opaque CUID2 ids resolve through the local wallet — the id is a
        // lookup key on the owner's own device, never a credential elsewhere.
        let jwt = token
        if (!token.includes('.') && /^[a-z0-9]{20,30}$/.test(token)) {
            jwt = await this._resolveStoredToken(token)
            if (!jwt) return { allowed: false, reason: `Token not found: ${token.substring(0, 8)}...` }
        }

        // Two forms of the request path for scope checking: serverName-rooted
        // (route-rooted tokens) and directory-relative (legacy fallback).
        const instanceId = getInstanceId()
        const sn = this.serverName
        let tail = '/'
        if (data.path) {
            let p = data.path
            if (instanceId && p.startsWith(`/${instanceId}`)) p = p.substring(`/${instanceId}`.length)
            else if (sn && p.startsWith(`/${sn}`)) p = p.substring(`/${sn}`.length)
            tail = p || '/'
        }
        if (!tail.startsWith('/')) tail = '/' + tail
        const dirRelative = tail
        const serverRooted = sn ? `/${sn}${tail === '/' ? '/' : tail}`.replace(/\/+$/, '/') : tail

        if (!this._keyResolver || !this._authUrl) {
            return { allowed: false, reason: 'Auth not initialized — key resolver or auth URL unavailable' }
        }

        try {
            const result = await this._verify(jwt)
            if (!result.valid) return { allowed: false, reason: result.error }

            if (result.revocationCheck === 'degraded') {
                console.warn(
                    '[browserdav] allowed with an UNCHECKED revocation status —',
                    'a revocation source was unreachable. Fail-open is deliberate;',
                    'the credential is bounded by its own expiry, not by us.'
                )
            }

            // Self-rooted acceptance: only chains rooted in the owner's keys
            // open this server. rootSub comes from the VERIFIED chain; the
            // owner was resolved at start (this context's user, or auth.owner
            // for a headless server that has none).
            const rootSub = result.rootSub
            const ownUserId = this._ownerUserId
            if (!rootSub || !ownUserId || rootSub !== ownUserId) {
                return { allowed: false, reason: 'Token was not issued by this server — foreign-rooted chains are not accepted' }
            }

            let accessResult = chains.checkScopeAccess(method, serverRooted, result.effectiveScope)
            if (!accessResult.allowed && dirRelative !== serverRooted) {
                accessResult = chains.checkScopeAccess(method, dirRelative, result.effectiveScope)
            }
            if (accessResult.allowed) {
                // Derive read/write path lists from the canonical grants — an
                // empty read grant means no read, never '*'.
                const scope = result.effectiveScope
                accessResult.user = {
                    paths: chains.pathsForPrivileges(scope, ['read']),
                    writePaths: chains.pathsForPrivileges(scope, ['bind', 'unbind', 'write-content', 'write-properties']),
                    isAnonymous: false,
                    scope,
                }
            }
            return accessResult
        } catch (err) {
            return { allowed: false, reason: `Chain verification error: ${err.message}` }
        }
    }

    /**
     * The one verification call — overridable in tests. Since 3.0.47 it is
     * routeAuth's verifyBearer, the verifier every peer route uses: an anchor
     * refused first, a chain resolved locally (the wallet, the link cache)
     * or from the auth server and verified link by link, a lone user token
     * by its signer's key; rooted in this server's owner — the page's user,
     * or the declared auth.owner of a headless server. The id → JWT step
     * stays this server's (_resolveStoredToken: the wallet, then the auth
     * server's chain-id redemption), and so does the two-path scope check.
     */
    async _verify(jwt) {
        const { verifyBearer } = await import('../routeAuth.js')
        const r = await verifyBearer(jwt, {
            authUrl: this._authUrl,
            owners: this._ownerUserId ? [this._ownerUserId] : [],
        })
        if (!r.valid) return { valid: false, error: r.reason }
        return { valid: true, rootSub: r.rootSub, effectiveScope: r.scope, revocationCheck: r.revocationCheck || 'confirmed', kind: r.kind }
    }

    /**
     * An opaque token id → its leaf JWT. The local wallet first — a server
     * that minted its own tokens has them there — then the auth server's
     * unauthenticated `GET /auth/chains/id/:id`, which is how every other
     * consumer (the service worker's short-code redeemer) presents an id.
     *
     * The fallback is what makes a declared-owner server usable at all: its
     * tokens were minted in the OWNER's session and live in the owner's
     * wallet, never in the page running the server. Found 2026-09-20 on the
     * first token ever presented to a headless fixture: "Token not found",
     * from a server that could not have had it. The id is the credential
     * either way; resolving it fetches nothing a bearer could not.
     *
     * Resolved JWTs are cached for the server's life — the id is stable and
     * the leaf's expiry is enforced at verification, not here.
     */
    async _resolveStoredToken(tokenId) {
        try {
            const tokens = await chains.listWalletTokens()
            const entry = tokens.find((t) => t.leafId === tokenId || t.storedTokenId === tokenId)
            if (entry?.leafJwt) return entry.leafJwt
        } catch {
            /* no wallet in this context — the fallback is the path */
        }
        if (!this._authUrl) return null
        this._resolvedIds ||= new Map()
        if (this._resolvedIds.has(tokenId)) return this._resolvedIds.get(tokenId)
        try {
            const resp = await fetch(`${this._authUrl}/chains/id/${encodeURIComponent(tokenId)}`)
            if (!resp.ok) return null
            const jwt = (await resp.text()).trim()
            if (!/^eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(jwt)) return null
            this._resolvedIds.set(tokenId, jwt)
            return jwt
        } catch {
            return null
        }
    }

    _extractToken(data) {
        const authHeader = data.headers?.authorization || data.headers?.Authorization
        if (authHeader && authHeader.startsWith('Bearer ')) {
            return authHeader.split(' ')[1]
        }
        if (data.query?.token) return data.query.token
        if (data.path && data.path.includes('?token=')) {
            const url = new URL(data.path, 'http://localhost')
            return url.searchParams.get('token')
        }
        return null
    }

    async _handleAuthVerify(data) {
        const tokenId = data.query?.tokenId
        let jwt = data.query?.token || null
        if (!jwt && !tokenId) return buildVerifyBadRequest()
        if (!jwt && tokenId) {
            jwt = await this._resolveStoredToken(tokenId)
            if (!jwt) return buildVerifyResponse({ valid: false, reason: 'not_found' })
        }
        if (!this._keyResolver || !this._authUrl) {
            return buildVerifyResponse({ valid: false, reason: 'verifier_unavailable' })
        }
        try {
            const result = await this._verify(jwt)
            if (!result.valid) {
                return buildVerifyResponse({ valid: false, reason: result.error || 'invalid' })
            }
            // Prefer the VERIFIED root authority over the presented payload's sub.
            let userId = result.rootSub || null
            let expiresAt = null
            try {
                const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
                if (!userId) userId = payload.sub || null
                if (payload.exp) expiresAt = new Date(payload.exp * 1000).toISOString()
            } catch { /* claims stay null — validity already established */ }
            return buildVerifyResponse({
                valid: true,
                userId,
                scope: result.effectiveScope || { paths: ['*'], writePaths: ['*'] },
                expiresAt,
            })
        } catch (err) {
            return buildVerifyResponse({
                valid: false,
                reason: `verification error: ${String(err.message || err).slice(0, 120)}`,
            })
        }
    }

    // ── sidecar public access ────────────────────────────────────────

    /**
     * .acequia-access.json, nearest wins, walking up from the request's own
     * directory. Unlike the app's version this navigates share-relative —
     * the share segment is resolved off the front first, so subdirectory
     * sidecars are actually found.
     */
    async _checkSidecarAccess(data) {
        const requestPath = safeDecodePath(this._getRequestPath(data))
        const resolved = this.manager.resolveInstance(requestPath)
        const instance = resolved?.instance
            || (this.manager.webdavInstances.size > 0
                ? this.manager.webdavInstances.values().next().value
                : null)
        if (!instance) return null
        const rootHandle = instance.filesystem?.fileSystemHandler?.rootHandle
            || instance.directoryHandle
        if (!rootHandle) return null

        const shareName = resolved?.pathName || instance.pathName
        const remaining = resolved ? resolved.remainingPath : requestPath
        const parts = remaining.split('/').filter(Boolean)
        const isDirectoryRequest = remaining.endsWith('/') || parts.length === 0
        const filename = isDirectoryRequest ? null : parts.pop()
        const dirParts = [...parts]

        for (let depth = dirParts.length; depth >= 0; depth--) {
            const currentDirParts = dirParts.slice(0, depth)
            const dirKey = '/' + [shareName, ...currentDirParts].join('/')

            const cached = this._sidecarCache.get(dirKey)
            let sidecarData
            if (cached && Date.now() < cached.expiry) {
                sidecarData = cached.data
            } else {
                const dirHandle = await this._navigateToDir(rootHandle, currentDirParts)
                sidecarData = dirHandle ? await this._readSidecar(dirHandle) : null
                this._sidecarCache.set(dirKey, { data: sidecarData, expiry: Date.now() + 60000 })
            }
            if (!sidecarData) continue

            const result = this._evaluateSidecar(sidecarData, dirKey, currentDirParts, dirParts, filename)
            if (result !== undefined) return result
        }
        return null
    }

    /** @returns {{paths: string[]}} granted, null denied (stop), undefined not applicable (keep walking) */
    _evaluateSidecar(sidecar, dirKey, sidecarDirParts, requestDirParts, filename) {
        // A sidecar that is not read:anonymous blocks the walk — nearest wins.
        if (sidecar.read !== 'anonymous') return null
        const isExactDir = sidecarDirParts.length === requestDirParts.length
        if (!isExactDir && !sidecar.recursive) return undefined
        if (filename && this._matchesDenyPattern(filename, sidecar.denyPatterns)) return null
        const scopePath = dirKey === '/' ? '/*' : dirKey + '/*'
        return { paths: [scopePath] }
    }

    async _navigateToDir(rootHandle, pathParts) {
        let handle = rootHandle
        for (const part of pathParts) {
            try {
                handle = await handle.getDirectoryHandle(part)
            } catch {
                return null
            }
        }
        return handle
    }

    async _readSidecar(dirHandle) {
        try {
            const fileHandle = await dirHandle.getFileHandle('.acequia-access.json')
            const file = await fileHandle.getFile()
            const parsed = JSON.parse(await file.text())
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
            return parsed
        } catch {
            return null
        }
    }

    _matchesDenyPattern(filename, patterns) {
        if (!patterns || !Array.isArray(patterns)) return false
        for (const pattern of patterns) {
            if (pattern === filename) return true
            if (pattern === '.*' && filename.startsWith('.')) return true
            if (pattern.startsWith('*.') && filename.endsWith(pattern.slice(1))) return true
        }
        return false
    }

    // ── per-request helpers ──────────────────────────────────────────

    _getRequestPath(data) {
        if (!data.path) return '/'
        const instancePrefix = `/${getInstanceId()}`
        const serverNamePrefix = this.serverName ? `/${this.serverName}` : null
        let requestPath = data.path
        if (requestPath.startsWith(instancePrefix)) {
            requestPath = requestPath.substring(instancePrefix.length)
        } else if (serverNamePrefix && requestPath.startsWith(serverNamePrefix)) {
            requestPath = requestPath.substring(serverNamePrefix.length)
        }
        return requestPath || '/'
    }

    /**
     * Per-member visibility, one decision for every listing (PROPFIND and
     * the JSON/HTML listings). Returns true (shown) or a status: the member
     * exists and this
     * caller may not read it. PROPFIND marks such a member with that status
     * and no properties (RFC 4918 §9.1: every member gets a response
     * element); the browser listings hide it. 401 when signing in could
     * help (an anonymous visitor), 403 when it cannot (a token whose scope
     * does not reach it) — the status a direct request for the member gets.
     * Before 2026-10-06 scope was checked on the request path only, so a
     * token scoped to one folder listed every sibling with its size and
     * mtime. (projects: webdavsync-reports-unreadable-destination-
     * directories-as-missing-files)
     *
     * PROPFIND passes the full route path (/<instance|server>/<share>/…);
     * the listings pass a share-relative one (/…). Both become the gate's
     * share-rooted tail (/<share>/…), checked the way _checkRequestAuth
     * checks the request: server-rooted first, then directory-relative.
     */
    _makeEntryFilter(data) {
        const user = data.user || {}
        const dotfiles = this._makeDotfileFilter(user)
        // As the gate answers: no token presented 401, a token that does not reach 403
        const refusal = this._extractToken(data) ? 403 : 401
        const sn = this.serverName
        const iid = getInstanceId()
        const prefixes = [iid && `/${iid}/`, sn && `/${sn}/`].filter(Boolean)
        const shareName = this.manager.resolveInstance(data.path || '')?.pathName
            || (this.manager.webdavInstances.size > 0 ? this.manager.webdavInstances.values().next().value?.pathName : '')
            || ''
        const unrestricted = !user.scope && !user.isAnonymous && (user.paths || []).includes('*')
        // A direct request passes on the token's scope, else on a public
        // sidecar (nearest wins, per directory) — so a member is readable on
        // either, asked the same way. Sidecar reads are cached (60 s).
        const tokenAllows = (tail) => {
            if (!user.scope) return !user.isAnonymous && chains.matchesPath(tail, user.paths || [])
            return chains.checkScopeAccess('GET', sn ? `/${sn}${tail}` : tail, user.scope).allowed
                || chains.checkScopeAccess('GET', tail, user.scope).allowed
        }
        const sidecarAllows = async (tail, kind) =>
            !!(await this._checkSidecarAccess({ path: `${sn ? `/${sn}` : ''}${tail}${kind === 'directory' ? '/' : ''}` }))
        const filter = async (name, childPath, kind) => {
            // A dotfile the caller may not read is a refused member like any
            // other: marked in PROPFIND, hidden by the listings.
            if (!dotfiles(name, childPath)) return refusal
            if (unrestricted) return true
            const prefix = prefixes.find((p) => childPath.startsWith(p))
            const tail = prefix
                ? childPath.slice(prefix.length - 1)
                : `/${shareName}${childPath.startsWith('/') ? '' : '/'}${childPath}`
            if (tokenAllows(tail)) return true
            return (await sidecarAllows(tail, kind)) ? true : refusal
        }
        // Listings are cached and deduplicated per path; the key must also
        // name whose view this is, or one caller's listing serves another's.
        filter.cacheKey = JSON.stringify([
            unrestricted ? '*' : user.isAnonymous ? 'anonymous' : 'token',
            unrestricted ? null : (user.scope ?? user.paths ?? []),
            user.writePaths ?? [],
        ])
        return filter
    }

    _makeDotfileFilter(user) {
        const writePaths = user?.writePaths || []
        return (name, childPath) => {
            if (!name.startsWith('.')) return true
            if (DOTFILE_READER_ALLOWLIST.includes(name)) return true
            return chains.matchesPath(childPath, writePaths)
        }
    }

    _selfHosts() {
        const hosts = new Set()
        try {
            hosts.add(new URL(getDiscoveryOrigin()).host)
        } catch { /* discovery origin unavailable — page host still applies */ }
        if (typeof window !== 'undefined' && window.location?.host) {
            hosts.add(window.location.host)
        }
        return [...hosts]
    }

    /**
     * COPY/MOVE two-leg authorizer, consulted by the filesystem after it has
     * localized the Destination header. The gate judges the request path
     * only; without this a read-only grant could COPY anywhere and an
     * overwrite never cost `unbind`.
     */
    _makeTransferAuthorizer(data) {
        const user = data.user
        const scope = user?.scope ?? { paths: user?.paths || [], writePaths: user?.writePaths || [] }
        const requestPath = safeDecodePath(this._getRequestPath(data) || '/')
        const sn = this.serverName
        return ({ method, sourcePath, destPath, destExists }) => {
            // A pull has no local source: the destination leg alone, judged
            // as the PUT it becomes — bind to create, write-content to replace.
            if (method === 'PULL') {
                const prefix = requestPath.endsWith(destPath)
                    ? requestPath.slice(0, requestPath.length - destPath.length)
                    : requestPath.replace(/\/+$/, '')
                const dirRelative = prefix + destPath
                const rooted = sn ? `/${sn}${dirRelative}` : dirRelative
                const verdict = chains.checkScopeAccess('PUT', rooted, scope, { exists: destExists })
                if (verdict.allowed || rooted === dirRelative) return verdict.allowed ? verdict : { allowed: false, reason: `destination: ${verdict.reason}` }
                const fallback = chains.checkScopeAccess('PUT', dirRelative, scope, { exists: destExists })
                return fallback.allowed ? fallback : { allowed: false, reason: `destination: ${verdict.reason}` }
            }
            const src = sourcePath || '/'
            const prefix = requestPath.endsWith(src)
                ? requestPath.slice(0, requestPath.length - src.length)
                : requestPath.replace(/\/+$/, '')
            const dirRelative = { sourcePath: prefix + src, destPath: prefix + destPath }
            const rooted = sn
                ? { sourcePath: `/${sn}${dirRelative.sourcePath}`, destPath: `/${sn}${dirRelative.destPath}` }
                : dirRelative
            const verdict = chains.checkTransferAccess(scope, { method, ...rooted, destExists })
            if (verdict.allowed || rooted === dirRelative) return verdict
            const fallback = chains.checkTransferAccess(scope, { method, ...dirRelative, destExists })
            return fallback.allowed ? fallback : verdict
        }
    }

    async _bufferBodyStream(data) {
        const buf = await new Response(data.bodyStream).arrayBuffer()
        delete data.bodyStream
        data.rawBody = buf
        const ct = (data.headers?.['content-type'] || '').toLowerCase()
        const asText = () => new TextDecoder().decode(buf)
        if (ct.includes('application/json')) {
            const text = asText()
            try { data.body = JSON.parse(text) } catch { data.body = text }
        } else if (ct.includes('text/')) {
            data.body = asText()
        } else if (!ct || ct.includes('application/x-www-form-urlencoded')) {
            const text = asText()
            try { data.body = JSON.parse(text) } catch { data.body = text }
        } else {
            data.body = buf
        }
    }

    // ── directory JSON (the SW mount probe contract) ─────────────────

    _resolveInstance(data) {
        const requestPath = data.url || data.path || ''
        const resolved = this.manager.resolveInstance(requestPath)
        if (resolved) {
            return { filesystem: resolved.instance.filesystem, remainingPath: resolved.remainingPath }
        }
        if (this.manager.webdavInstances.size > 0) {
            const instance = this.manager.webdavInstances.values().next().value
            return { filesystem: instance.filesystem, remainingPath: '/' }
        }
        return null
    }

    _servePages() {
        return this.options.servePages !== false
    }

    /** The file-browser page for a directory of this instance. */
    _fileBrowserPage(dirPath, dirName, data) {
        const token = this._extractToken(data)
        const rootUrl = this.url
        // The path-mode base the element navigates within: the arrival route
        // prefix (serverName or instance id, whichever the client used) with
        // dirPath stripped off the end.
        let routePrefix = `/${encodeURIComponent(this._routeSegment())}/`
        if (data?.path) {
            const normalized = dirPath === '/' ? '' : dirPath.replace(/\/$/, '')
            const fullPath = data.path.endsWith('/') ? data.path : data.path + '/'
            routePrefix = normalized && fullPath.endsWith(normalized + '/')
                ? fullPath.slice(0, -normalized.length)
                : fullPath
        }
        const basePath = `/groups/${encodeURIComponent(this.groupName)}${routePrefix}`
        return fileBrowserPage({ dirPath, dirName, token, rootUrl, basePath })
    }

    /** A share directory's own index.html, if it has one; null otherwise. */
    async _tryServeIndexHtml(data) {
        if (!this.manager.hasActiveInstances()) return null
        const resolved = this._resolveInstance(data)
        if (!resolved) return null
        const { filesystem, remainingPath } = resolved
        const indexPath = remainingPath.endsWith('/') ? remainingPath + 'index.html' : remainingPath + '/index.html'
        try {
            const fileHandle = await filesystem.getFileHandle(indexPath)
            if (!fileHandle || fileHandle.kind === 'directory') return null
            const file = await fileHandle.getFile()
            return {
                status: 200,
                headers: { 'Content-Type': 'text/html; charset=utf-8', 'Cache-Control': 'no-cache' },
                body: await file.text(),
            }
        } catch {
            return null
        }
    }

    async _handleAcequiaHtml(data) {
        const resolved = this._resolveInstance(data)
        if (!resolved) return { status: 503, headers: { 'Content-Type': 'text/plain' }, body: 'No filesystem available' }
        const entries = await this._gatherDirectoryEntries(resolved.filesystem, resolved.remainingPath, data.entryFilter)
        if (entries === null) return { status: 404, headers: { 'Content-Type': 'text/plain' }, body: 'Not found' }
        return plainIndexPage({ pathname: resolved.remainingPath, entries })
    }

    _handleAcequiaEditor(data) {
        const reqPath = this._getRequestPath(data)
        const fileUrl = this._publicHrefBase(data) + reqPath.replace(/^\//, '')
        return editorPage({ reqPath, fileUrl, token: this._extractToken(data) })
    }

    async _handleAcequiaJson(data, { probe = false } = {}) {
        const resolved = this._resolveInstance(data)
        if (!resolved) return { status: 503, headers: { 'Content-Type': 'application/json' }, body: '[]' }
        const entries = await this._gatherDirectoryEntries(resolved.filesystem, resolved.remainingPath, data.entryFilter)
        if (entries === null) {
            if (probe) return null
            return { status: 404, headers: { 'Content-Type': 'application/json' }, body: '[]' }
        }
        return {
            status: 200,
            headers: {
                'Content-Type': 'application/json',
                'X-Acequia-Directory-JSON': 'true',
                'Cache-Control': 'private, no-cache',
            },
            body: JSON.stringify(entries),
        }
    }

    async _gatherDirectoryEntries(filesystem, dirPath, entryFilter) {
        let fileHandle
        try {
            fileHandle = await filesystem.getFileHandle(dirPath, { getDirectory: true })
        } catch {
            return null
        }
        if (!fileHandle || fileHandle.kind !== 'directory') return null

        const entries = []
        for await (const entry of fileHandle.values()) {
            const childPath = dirPath.endsWith('/') ? dirPath + entry.name : dirPath + '/' + entry.name
            // true only: a status means refused, and a listing hides it
            if (entryFilter && (await entryFilter(entry.name, childPath, entry.kind)) !== true) continue
            const isDir = entry.kind === 'directory'
            let size = 0
            let lastmod = new Date().toUTCString()
            let mime = ''
            if (!isDir) {
                try {
                    const file = await entry.getFile()
                    size = file.size
                    lastmod = file.lastModified ? new Date(file.lastModified).toUTCString() : lastmod
                    mime = filesystem.detectMimeType(entry.name)
                } catch { /* stat best-effort */ }
            }
            entries.push({ filename: childPath, basename: entry.name, type: isDir ? 'directory' : 'file', size, lastmod, mime })
        }
        entries.sort((a, b) => {
            if (a.type !== b.type) return a.type === 'directory' ? -1 : 1
            return a.basename.localeCompare(b.basename)
        })
        return entries
    }

    // ── root listing ─────────────────────────────────────────────────

    _publicHrefBase(data) {
        return (
            forwardedHrefBase({ headers: data?.headers, path: data?.path }) ||
            this.url
        )
    }

    _rootDirectoryJSON() {
        const directories = this.manager.getAllInstances().map((instance) => ({
            name: instance.pathName,
            displayName: instance.displayName,
            type: 'directory',
            isDirectory: true,
            size: null,
        }))
        return {
            status: 200,
            headers: { 'Content-Type': 'application/json; charset=utf-8' },
            body: JSON.stringify({ path: '/', type: 'directory', entries: directories }, null, 2),
        }
    }

    _rootPropfind(data) {
        const instances = this.manager.getAllInstances()
        const baseUrl = this._publicHrefBase(data).replace(/\/$/, '')
        let depth = 1
        if (data.headers?.depth !== undefined) {
            depth = /^infinity$/i.test(String(data.headers.depth))
                ? Infinity
                : parseInt(data.headers.depth) || 1
        }
        let xml = `<?xml version="1.0" encoding="utf-8"?>\n<D:multistatus xmlns:D="DAV:">\n`
        const entry = (href, displayname) =>
            `  <D:response>\n    <D:href>${href}</D:href>\n    <D:propstat>\n      <D:prop>\n` +
            `        <D:resourcetype><D:collection/></D:resourcetype>\n` +
            `        <D:displayname>${displayname}</D:displayname>\n` +
            `        <D:getcontenttype>httpd/unix-directory</D:getcontenttype>\n` +
            `      </D:prop>\n      <D:status>HTTP/1.1 200 OK</D:status>\n    </D:propstat>\n  </D:response>\n`
        xml += entry(`${baseUrl}/`, 'WebDAV Root')
        if (depth > 0) {
            for (const instance of instances) {
                xml += entry(`${baseUrl}/${escapeXml(instance.pathName)}/`, escapeXml(instance.displayName))
            }
        }
        xml += `</D:multistatus>`
        return {
            status: 207,
            headers: { 'Content-Type': 'application/xml; charset=utf-8', DAV: '1, 2' },
            body: xml,
        }
    }
}
